/* ProfilePage: Fixed Avatar Persistence and Password Logic */
import React, { useState } from 'react';
import { useStore } from '../store/StoreContext';
import { Input } from '../components/UI/Input';
import { Button } from '../components/UI/Button';
import { ImageUpload } from '../components/UI/ImageUpload';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

export const ProfilePage = () => {
  const { state, refresh } = useStore();
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: state.currentUser?.name || '',
    avatar: state.currentUser?.avatar || '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSuccess(false);

    try {
      // 1. Update Profile Data in users_2024 table
      const { error: profileErr } = await supabase
        .from('users_2024')
        .update({
          name: formData.name,
          avatar: formData.avatar
        })
        .eq('id', state.currentUser.id);

      if (profileErr) throw profileErr;

      // 2. Handle Password Change via Supabase Auth if provided
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error("Passwords do not match");
        }
        const { error: authErr } = await supabase.auth.updateUser({
          password: formData.newPassword
        });
        if (authErr) throw authErr;
      }

      setSuccess(true);
      setFormData(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
      await refresh();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 h-full overflow-y-auto bg-base-900 custom-scrollbar">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Account Settings</h1>
          <p className="text-sm text-gray-400">Manage your profile and security preferences.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-1 space-y-6">
            <div className="bg-base-800 rounded-2xl border border-base-700 p-6 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full border-4 border-base-700 overflow-hidden mb-4 shadow-xl bg-base-900 flex items-center justify-center">
                {formData.avatar ? (
                  <img src={formData.avatar} className="w-full h-full object-cover" alt="Avatar" />
                ) : (
                  <SafeIcon name="User" className="w-10 h-10 text-gray-600" />
                )}
              </div>
              <h3 className="font-bold text-lg text-white">{state.currentUser?.name}</h3>
              <p className="text-[10px] text-primary uppercase font-black tracking-widest mt-1 px-3 py-1 bg-primary/10 rounded-full">
                {state.currentUser?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>

          <div className="md:col-span-2 space-y-6">
            <form onSubmit={handleUpdateProfile} className="bg-base-800 rounded-2xl border border-base-700 overflow-hidden shadow-xl">
              <div className="p-6 border-b border-base-700 bg-base-900/30">
                <h3 className="font-bold flex items-center gap-2 text-white">
                  <SafeIcon name="User" className="text-primary w-4 h-4" /> Personal Information
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input label="Display Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                  <Input label="Email Address" value={state.currentUser?.email} disabled className="opacity-50" />
                </div>
                <div className="pt-4 border-t border-base-700/50">
                  <ImageUpload 
                    label="Profile Picture" 
                    currentUrl={formData.avatar} 
                    onUpload={(url) => setFormData({ ...formData, avatar: url })} 
                  />
                </div>
                <div className="pt-6 border-t border-base-700">
                  <h3 className="font-bold text-sm mb-4 flex items-center gap-2 text-white">
                    <SafeIcon name="Shield" className="text-accent w-4 h-4" /> Security
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="New Password" type="password" value={formData.newPassword} onChange={e => setFormData({ ...formData, newPassword: e.target.value })} placeholder="Leave blank to keep current" />
                    <Input label="Confirm New Password" type="password" value={formData.confirmPassword} onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })} />
                  </div>
                </div>
              </div>
              <div className="p-6 bg-base-900/30 border-t border-base-700 flex justify-between items-center">
                {success ? (
                  <div className="flex items-center gap-2 text-green-400 text-xs font-bold">
                    <SafeIcon name="CheckCircle" className="w-4 h-4" /> Changes Saved
                  </div>
                ) : <div />}
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? <SafeIcon name="Loader" className="w-4 h-4 animate-spin mr-2" /> : 'Save Profile'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};